import { useCallback, useEffect, useRef, useState } from "react";
import { adminApi } from "./api";
import type { SystemStatus } from "./types";

export type ResourceStatus = "loading" | "ready" | "error";

export interface Resource<T> {
  status: ResourceStatus;
  data: T | null;
  /** Transport or server error message, only set when status is "error". */
  error: string;
  reload: () => void;
}

/**
 * Fetches one admin endpoint and tracks loading/ready/error honestly.
 * Never fabricates rows: on failure status is "error" and data stays null.
 */
export function useAdminResource<T>(path: string): Resource<T> {
  const [status, setStatus] = useState<ResourceStatus>("loading");
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = requestId.current + 1;
    requestId.current = id;
    setStatus("loading");
    setError("");
    try {
      const result = await adminApi<T>(path);
      if (requestId.current !== id) return;
      setData(result);
      setStatus("ready");
    } catch (reason) {
      if (requestId.current !== id) return;
      setError(reason instanceof Error ? reason.message : "Unbekannter Fehler");
      setStatus("error");
    }
  }, [path]);

  useEffect(() => { void load(); }, [load]);
  return { status, data, error, reload: load };
}

/** Overall health derived from the three service states (no invented fields). */
export function overallHealth(parts: Array<string | undefined>): "checking" | "healthy" | "mixed" | "down" {
  const states = parts.filter(Boolean) as string[];
  if (states.length === 0) return "checking";
  if (states.every((s) => s === "healthy")) return "healthy";
  if (states.some((s) => s === "down")) return "down";
  return "mixed";
}

export function useSystemState() {
  return useAdminResource<SystemStatus>("/system");
}
